import { useRetailerCoreWithSelector } from "@faire/retailer-visitor-shared/hooks/retailer/RetailerCoreProvider";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { createContext, useContext, useMemo } from "react";
import { createStore, StoreApi, useStore } from "zustand";

interface RetailerBusinessCategory {
  businessCategory: keyof typeof IRetailer.BusinessCategory | undefined;
  isOnlineOnlyRetailer: boolean;
}

const RetailerBusinessCategoryContext = createContext<
  StoreApi<RetailerBusinessCategory> | undefined
>(undefined);

export const RetailerBusinessCategoryProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const initialRetailer = getRetailer();
  const retailer = useRetailerCoreWithSelector((state) => state.retailer);

  const store = useMemo(
    () =>
      createStore<RetailerBusinessCategory>(() => {
        const businessCategory =
          retailer?.business_category ?? initialRetailer?.business_category;
        const isOnlineOnlyRetailer =
          !!retailer?.online_only ||
          businessCategory === IRetailer.BusinessCategory.ONLINE_ONLY;

        return {
          businessCategory,
          isOnlineOnlyRetailer,
        };
      }),
    [
      retailer?.business_category,
      initialRetailer?.business_category,
      retailer?.online_only,
    ]
  );

  return (
    <RetailerBusinessCategoryContext.Provider value={store}>
      {children}
    </RetailerBusinessCategoryContext.Provider>
  );
};

export const useRetailerBusinessCategory = () => {
  const context = useContext(RetailerBusinessCategoryContext);
  if (!context) {
    throw new Error(
      "RetailerBusinessCategoryContext not found. Must be used within RetailerBusinessCategoryProvider"
    );
  }
  return useStore(context);
};
